import React, { type ReactNode, useCallback, useMemo } from "react"

import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { Dropdown, Popup } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"

import api from "api"
import util from "utils/utils"

import type { ILaneData } from "./Board"

export type ILaneSettings = {
  laneId: string
  editLaneName: () => void
  editLaneDescription: () => void
  updateLane: (data: Partial<OpenAPI.Schemas["ProjectLane"]>, onComplete?: () => void, onError?: () => void) => void
  lanes: ILaneData[]
  ideas: OpenAPI.GET<"/challenges/{id}/ideas">["response"]["ideas"]
  handleWebsocketLanes: () => void
  handleWebsocketIdeas: () => void
  forId: string
  trigger: ReactNode
  reorderLaneIdeas: (sort: string, sortDirection: string) => void
  checkUpdateProjectLaneMulti: (ideaIds: string[], targetLane: string) => void
}

export const DropdownLaneSettings = ({
  laneId,
  editLaneName,
  editLaneDescription,
  updateLane,
  lanes,
  ideas,
  handleWebsocketLanes,
  handleWebsocketIdeas,
  forId,
  trigger,
  reorderLaneIdeas,
  checkUpdateProjectLaneMulti,
}: ILaneSettings) => {
  const lane = useMemo(() => lanes.find((l) => l._id === laneId), [lanes, laneId])
  const ideasOnLane = useMemo(
    () => ideas.filter((i) => i?.projectManagement?.boards?.find((b) => b.forId === i.challenge)?.lane === laneId),
    [ideas, laneId],
  )
  const { t } = useTranslation()

  const currentLaneId = lane?._id || "default"
  const isDefaultLane = currentLaneId === "default"
  const currentLaneMetaType = lane?.metaType || "normal"

  const LaneTypeOptions: {
    key: OpenAPI.Schemas["projectLanes"]["metaType"]
    value: OpenAPI.Schemas["projectLanes"]["metaType"]
    text: string
  }[] = [
    {
      key: "normal",
      value: "normal",
      text: "Normal lane",
    },
    {
      key: "notProgressing",
      value: "notProgressing",
      text: `${t("common:capitalise", { key: "generic.ideas" })} not progressing`,
    },
    {
      key: "completed",
      value: "completed",
      text: `${t("common:capitalise", { key: "generic.ideas" })} completed`,
    },
    {
      key: "resolved",
      value: "resolved",
      text: `${t("common:capitalise", { key: "generic.ideas" })} resolved`,
    },
  ]

  const LaneSortOptions: { text: string; value: string }[] = [
    {
      value: "projectUpdated",
      text: "Last update",
    },
    {
      value: "projectDeadline",
      text: "Project deadline",
    },
    {
      value: "ideaCreated",
      text: `${t("common:capitalise", { key: "generic.idea" })} creation`,
    },
    {
      value: "alphabetical",
      text: "Card name",
    },
    {
      value: "assessmentScore",
      text: "Average assessment score",
    },
  ]

  const moveLane = useCallback(
    (direction) => {
      if (direction === -1 && lane.order < 1) return
      if (direction === 1 && lane.order >= lanes.length - 1) return
      api.boards.updateProjectLane(
        forId,
        currentLaneId,
        { order: lane.order + direction },
        () => {
          api.boards.getProjectLanes(
            forId,
            () => {
              handleWebsocketLanes()
              api.queryClient.invalidateQueries({ queryKey: ["boards", forId, "lanes"] })
            },
            () => {},
          )
        },
        (err) => toast.error(err.message),
      )
    },
    [lane, forId, currentLaneId, handleWebsocketLanes, lanes.length],
  )

  const deleteLane = useCallback(() => {
    util
      .confirm(
        "Really delete this lane?",
        `All ${t("generic.ideas")} on this lane will be moved back to the default lane.`,
      )
      .then(() => {
        api.boards.deleteProjectLane(
          forId,
          currentLaneId,
          () => {
            handleWebsocketLanes()
            handleWebsocketIdeas()
            api.queryClient.invalidateQueries({ queryKey: ["boards", forId, "lanes"] })
            api.queryClient.invalidateQueries({ queryKey: ["challenges", forId, "ideas"] })
          },
          (err) => toast.error(err.message),
        )
      })
      .catch(() => {})
  }, [forId, currentLaneId, handleWebsocketLanes, handleWebsocketIdeas, t])

  return (
    <Dropdown
      trigger={trigger}
      on="click"
      icon={null}
      closeOnChange={false}
      closeOnEscape={false}
      pointing="left"
      upward={false}
    >
      <Dropdown.Menu>
        <Dropdown.Header content="Lane Settings" />
        <Dropdown.Divider />
        {lanes.length > 1 && !isDefaultLane ? (
          <Dropdown item pointing="left" text="Move lane">
            <Dropdown.Menu>
              {lane.order > 0 ? <Dropdown.Item content="Move left" onClick={() => moveLane(-1)} /> : null}
              {lane.order < lanes.length - 1 ? (
                <Dropdown.Item content="Move right" onClick={() => moveLane(1)} />
              ) : null}
            </Dropdown.Menu>
          </Dropdown>
        ) : null}
        {ideasOnLane?.length ? (
          <Dropdown item pointing="left" upward={false} text={`Reorder ${t("generic.ideas")} by...`}>
            <Dropdown.Menu>
              {LaneSortOptions.map((l, i) => (
                <React.Fragment key={l.value}>
                  {i !== 0 ? <Dropdown.Divider /> : null}
                  <Dropdown.Item content={`${l.text} (descending)`} onClick={() => reorderLaneIdeas(l.value, "desc")} />
                  <Dropdown.Item content={`${l.text} (ascending)`} onClick={() => reorderLaneIdeas(l.value, "asc")} />
                </React.Fragment>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        ) : null}

        {!isDefaultLane ? (
          <Popup
            trigger={
              <Dropdown item pointing="left" text="Change lane type">
                <Dropdown.Menu>
                  {LaneTypeOptions.filter((l) => l.value !== currentLaneMetaType).map((l) => (
                    <Dropdown.Item key={l.key} content={l.text} onClick={() => updateLane({ metaType: l.value })} />
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            }
            content={`${t("common:capitalise", { key: "generic.ideas" })} in typed lanes do not send notifications to assignees when the ${t("generic.idea")} has not been updated in a while`}
          />
        ) : null}

        {ideasOnLane?.length && lanes?.length > 1 ? (
          <Dropdown item pointing="left" text={`Move all ${t("generic.ideas")} to other lane`}>
            <Dropdown.Menu>
              {lanes
                .filter((l) => l._id !== currentLaneId)
                .map((l) => (
                  <Dropdown.Item
                    key={l._id}
                    content={l.name}
                    onClick={() =>
                      checkUpdateProjectLaneMulti(
                        ideasOnLane.map((idea) => idea._id),
                        l._id,
                      )
                    }
                  />
                ))}
            </Dropdown.Menu>
          </Dropdown>
        ) : null}
        {!isDefaultLane ? <Dropdown.Divider /> : null}
        <Dropdown.Item content="Edit lane name" onClick={() => editLaneName()} />
        <Dropdown.Item content="Edit lane description" onClick={() => editLaneDescription()} />
        {!isDefaultLane ? (
          <Dropdown.Item content="Delete lane" style={{ color: "#db2828" }} onClick={() => deleteLane()} />
        ) : null}
      </Dropdown.Menu>
    </Dropdown>
  )
}
